import React from 'react';
import {useForm} from 'react-hook-form';
import * as z from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from '@/components/ui/form';
import {Textarea} from '@/components/ui/textarea';
import {Input} from '@/components/ui/input';
import { Checkbox } from "@/components/ui/checkbox"

const formSchema = z.object({
    case_name: z.string({
        required_error: "Please enter case title",
    }).min(3, {
        message: "Please enter case title",
    }),
    case_description: z.string({
        required_error: "Please enter case description",
    }).min(10, {
        message: "Please enter case description",
    }),
    key_question: z.string({
        required_error: "Please enter key question",
    }),
    is_anonymous: z.boolean().default(false).optional(),
});

formSchema.required({
    case_name: true,
    case_description: true,
    key_question: true,
});

type NewCaseFormProps = {
    onSubmit: (values: z.infer<typeof formSchema>) => void;
    formId?: string;
}

export const NewCaseForm = (props: NewCaseFormProps) => {
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            is_anonymous: false,
        },
    })

    const onSubmit = (values: z.infer<typeof formSchema>) => {
        if (typeof props.onSubmit === 'function') {
            props.onSubmit(values);
        }
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" id={props.formId}>
                <FormField
                    control={form.control}
                    name="case_name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Case title</FormLabel>
                            <FormControl>
                                <Input {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="case_description"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                                <Textarea placeholder="Write case description" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="key_question"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Key question to be answered</FormLabel>
                            <FormControl>
                                <Textarea placeholder="Write key question" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="is_anonymous"
                    render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                                <Checkbox
                                    checked={field.value}
                                    onCheckedChange={(value) => {
                                        field.onChange(!!value);
                                    }}
                                />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                                <FormLabel>
                                    Submit without exposing my name to public
                                </FormLabel>
                            </div>
                        </FormItem>
                    )}
                />
            </form>
        </Form>
    )
};
